import { useState } from 'react';
import { AlertCircle, CheckCircle, ChevronDown, ChevronUp, XCircle } from 'react-feather';
import { format } from 'date-fns';
import Table from '../../../components/Table/Table';
import FlexWrapper from '../../FlexWrapper';
import DataUploadExpandedRow from './DataUploadExpandedRow';
import LoadingIcon from '../../../components/LoadingIcon';
import Tooltip from '../../../components/popups/Tooltip';
import "./DataManagerPage.scss";


const DataUploadTable = ({ data, loading, reupload, checkBeforeUpload, checkEncrypted, onHoldArray, setShowEncryptedDialog }) => {
	const [sortingColumn, setSortingColumn] = useState({ column: "Upload Date", ascending: false })
	const [expandedRows, setExpandedRows] = useState([])

	const sortTable = (column) => {
		if (sortingColumn.column === column) {
			setSortingColumn({ column, ascending: !sortingColumn.ascending })
		} else {
			setSortingColumn({ column, ascending: true })
		}
	}

	const handleSort = (a, b) => {
		switch (sortingColumn.column) {
			case "Upload Date":
				return sortingColumn.ascending ? a.uploadedDate.localeCompare(b.uploadedDate) : b.uploadedDate.localeCompare(a.uploadedDate);
			case "Files Processed":
				return sortingColumn.ascending ? a.status.numberCompleted - b.status.numberCompleted : b.status.numberCompleted - a.status.numberCompleted;
			case "Files Failed":
				return sortingColumn.ascending ? a.status.numberFailed - b.status.numberFailed : b.status.numberFailed - a.status.numberFailed;
			case "Files On hold":
				return sortingColumn.ascending ? a.uploadedFiles.filter(f => f.status === "ON_HOLD").length - b.uploadedFiles.filter(f => f.status === "ON_HOLD").length : b.uploadedFiles.filter(f => f.status === "ON_HOLD").length - a.uploadedFiles.filter(f => f.status === "ON_HOLD").length;
			case "Files Cancelled":
				return sortingColumn.ascending ? a.uploadedFiles.filter(f => f.status === "CANCELLED").length - b.uploadedFiles.filter(f => f.status === "CANCELLED").length : b.uploadedFiles.filter(f => f.status === "CANCELLED").length - a.uploadedFiles.filter(f => f.status === "CANCELLED").length;
			case "Uploaded By":
				return sortingColumn.ascending ? a.uploadedBy.name.localeCompare(b.uploadedBy.name) : b.uploadedBy.name.localeCompare(a.uploadedBy.name);
			default:
				return 0;
		}
	};

	const onHoldBatches = onHoldArray && onHoldArray.length > 0 ? onHoldArray.map(item => item.batchId) : []

	const colWidths = ["13%", "15%", "14%", "14%", "14%", "16%", "23%"]

	const headers = [
		{
			text: <HeaderText text="Upload Date" sortLabel="Upload Date" ascending={sortingColumn.ascending} currentSort={sortingColumn.column} sortTable={sortTable} />
		},
		{
			text: <HeaderText text="Files Processed" sortLabel="Files Processed" ascending={sortingColumn.ascending} currentSort={sortingColumn.column} sortTable={sortTable} />
		},
		{
			text: <HeaderText text="Files Failed" sortLabel="Files Failed" ascending={sortingColumn.ascending} currentSort={sortingColumn.column} sortTable={sortTable} />
		},
		{
			text: <HeaderText text="Files On Hold" sortLabel="Files On Hold" ascending={sortingColumn.ascending} currentSort={sortingColumn.column} sortTable={sortTable} />
		},
		{
			text: <HeaderText text="Files Cancelled" sortLabel="Files Cancelled" ascending={sortingColumn.ascending} currentSort={sortingColumn.column} sortTable={sortTable} />
		},
		{
			text: <HeaderText text="Uploaded By" sortLabel="Uploaded By" ascending={sortingColumn.ascending} currentSort={sortingColumn.column} sortTable={sortTable} />
		}
	]

	function renderStatusIcon(upload) {
		switch (upload.batchStatus) {
			case "FINISHED":
				return <CheckCircle size={20} color={"#56D1C3"} />
			case "FAILED":
				return <Tooltip label={upload.batchMessage}>
					<AlertCircle size={20} color={"#E66780"} />
				</Tooltip>
			case "RUNNING":
				return <LoadingIcon size={20} />
			default:
				break;
		}
	}

	function openDialog(e, upload) {
		e.stopPropagation();
		checkBeforeUpload(upload)
	}

	function openPasswordDialog(e, upload) {
		e.stopPropagation();
		const filteredFiles = upload.uploadedFiles.filter(f => f.status === 'ENCRYPTED');
		const updatedUploade = { ...upload, uploadedFiles: filteredFiles };
		checkEncrypted(updatedUploade, setShowEncryptedDialog)
	}

	const dataMap = () => {
		return data && data.length > 0 && data.sort(handleSort).map((upload, idx) => {
			const { numberCompleted, numberFailed, numberOfFiles } = upload.status
			const numberOnHold = upload.uploadedFiles.filter(f => f.status === "ON_HOLD").length
			const numberCancelled = upload.uploadedFiles.filter(f => f.status === "CANCELLED").length
			const numberEncrypted = upload.uploadedFiles.filter(f => f.status === "ENCRYPTED").length
			return <>
				<tr key={idx} className={`cursor-pointer data-processing-table-row ${expandedRows.includes(upload.batchId) ? "expanded" : ""}`} onClick={() => {
					expandedRows.includes(upload.batchId)
						? setExpandedRows(expandedRows.filter((val) => val !== upload.batchId))
						: setExpandedRows([...expandedRows, upload.batchId])
				}}>
					<td className={expandedRows.includes(upload.batchId) ? 'expanded-table-row' : ''}>{format(new Date(upload.uploadedDate), "dd/MM/yyyy")}</td>
					<td className={expandedRows.includes(upload.batchId) ? 'expanded-table-row' : ''}><FlexWrapper align='center' gap="s">{renderStatusIcon(upload)} {`${numberCompleted}/${numberOfFiles}`}</FlexWrapper></td>
					<td className={expandedRows.includes(upload.batchId) ? 'expanded-table-row' : ''}><FlexWrapper align='center' gap="s">{numberFailed >= 1 ? <AlertCircle size={20} color={"#E66780"} /> : ""} {`${numberFailed}/${numberOfFiles}`}</FlexWrapper></td>
					<td className={expandedRows.includes(upload.batchId) ? 'expanded-table-row' : ''}>
						<FlexWrapper align='center' gap="s">
							{numberOnHold >= 1
								? <AlertCircle size={20} color={"#E66780"} onClick={(e) => onHoldArray.length > 0 && onHoldBatches.includes(upload.batchId) ? openDialog(e, upload) : null} />
								: ""} {`${numberOnHold + numberEncrypted}/${numberOfFiles}`}
						</FlexWrapper>
					</td>
					<td className={expandedRows.includes(upload.batchId) ? 'expanded-table-row' : ''}><FlexWrapper align='center' gap="s">{numberCancelled >= 1 ? <XCircle size={20} color={"#E66780"} /> : ""} {`${numberCancelled}/${numberOfFiles}`}</FlexWrapper></td>
					<td className={expandedRows.includes(upload.batchId) ? 'expanded-table-row' : ''}><FlexWrapper align='center' gap="s">{upload?.uploadedBy?.name}</FlexWrapper></td>
					<td className={expandedRows.includes(upload.batchId) ? 'expanded-table-row' : ''}>
						<div style={{ display: "grid", gridTemplateColumns: '20px 1fr', gap: '0.5rem', alignItems: 'center' }} onClick={(e) => onHoldArray.length > 0 && onHoldBatches.includes(upload.batchId) ? openDialog(e, upload) : null}>
							{numberOnHold >= 1 || numberEncrypted >=1 ? <AlertCircle size={20} color={"#E66780"} /> : ""}
							{numberOnHold >= 1 || numberEncrypted >=1 ? <span style={{ fontSize: '0.9rem' }}>{`${numberOnHold + numberEncrypted > 1 ? "Files" : "File"} ${numberOnHold > 0 && numberEncrypted === 0 ? " on hold due to existing approved reviews found" : numberOnHold === 0 && numberEncrypted > 0 ? " encrypted" : "encrypted/on hold due to existing approved reviews found"}`}</span> : ""}
						</div>
					</td>
				</tr>
				{expandedRows.includes(upload.batchId) && (
					<DataUploadExpandedRow
						upload={upload}
						batchIndex={idx}
						reupload={reupload}
						openDialog={openDialog}
						openPasswordDialog={openPasswordDialog}
					/>
				)}
			</>
		})
	}

	if (loading) return <LoadingIcon centered />
	return (
		<div className="data-upload-table">
			<Table
				minWidth={1200}
				tableStyle={{ maxWidth: 1400 }}
				colWidths={colWidths}
				headers={headers}
				dataMap={dataMap()}
				id='data-manager-table'
			/>
		</div>
	);
}

export default DataUploadTable;

const HeaderText = ({ text, sortLabel, ascending, currentSort, sortTable }) => {
	return (
		<div className='sorting-header' onClick={() => sortTable(sortLabel)} >
			{text}
			{currentSort === sortLabel && ascending === true
				? <ChevronUp className="colour-text-primary" size={12} />
				: <ChevronDown className={currentSort === sortLabel ? "colour-text-primary" : ""} size={12} />
			}
		</div>
	)
}